import logger from 'helpers/logger';

import {
  CompanyMetadataAccountStatus,
  CompanyMetadataReviewStatus,
} from '../../../../__generated__/GQL';
import { ApplicationSelectionMandateeQuery } from './graphql/queries/ApplicationSelectionMandatee.gql';

export interface MandateeApplication {
  company: ApplicationSelectionMandateeQuery['viewer']['companies'][number];
  companyUser: ApplicationSelectionMandateeQuery['viewer']['companyUsers'][number];
}

const getMandateeApplications = (
  queryResult: ApplicationSelectionMandateeQuery,
): MandateeApplication[] => {
  const { companies, companyUsers, uid } = queryResult.viewer;

  return companies
    .map((company) => {
      const companyUser = companyUsers.find(
        (compUser) => compUser.companyProfileId === company.companyProfileId,
      );

      if (!companyUser) {
        throw new Error(
          `A CompanyUser should exist for companyProfileId: ${company.companyProfileId} and uid: ${uid}`,
        );
      }

      return {
        company,
        companyUser,
      };
    })
    .filter(({ companyUser }) => companyUser.isMandatee === true);
};

export const getApplications = (
  data: ApplicationSelectionMandateeQuery,
): Record<
  'pendingApplications' | 'refusedApplications' | 'validatedApplications',
  MandateeApplication[]
> => {
  const mandateeApplications = getMandateeApplications(data);

  const pendingApplications: MandateeApplication[] = [];
  const validatedApplications: MandateeApplication[] = [];
  const refusedApplications: MandateeApplication[] = [];

  mandateeApplications.forEach((application) => {
    if (
      application.company.metadata.reviewStatus ===
      CompanyMetadataReviewStatus.Refused
    ) {
      refusedApplications.push(application);
      return;
    }

    if (
      application.company.metadata.accountStatus ===
      CompanyMetadataAccountStatus.Validated
    ) {
      validatedApplications.push(application);
      return;
    }

    if (
      application.company.metadata.accountStatus ===
      CompanyMetadataAccountStatus.Pending
    ) {
      pendingApplications.push(application);
      return;
    }

    logger.error('Mandatee application not displayed', {
      context: {
        companyAccountStatus: application.company.metadata.accountStatus,
        companyProfileId: application.company.companyProfileId,
        companyReviewStatus: application.company.metadata.reviewStatus,
      },
    });
  });

  return { pendingApplications, refusedApplications, validatedApplications };
};
